import React, { useState } from 'react';
import { X, Loader2, Smartphone, Shuffle } from 'lucide-react';
import { downloadCodeApi } from '../lib/api';

interface FileItem {
  file_id: string;
  original_filename: string;
}

interface GeneratedCode {
  file_id: string;
  code: string;
  download_filename: string;
}

interface BulkDownloadCodeModalProps {
  files: FileItem[];
  folderMap?: Record<string, string>;
  label: string;
  onClose: () => void;
  onReady: (filenameOverrides: Record<string, string>, folderMap?: Record<string, string>) => void;
}

type CodeType = 'normal' | 'flexible';

export function BulkDownloadCodeModal({ files, folderMap, label, onClose, onReady }: BulkDownloadCodeModalProps) {
  const [codeTypes, setCodeTypes] = useState<Record<string, CodeType>>(
    () => Object.fromEntries(files.map(f => [f.file_id, 'normal' as CodeType]))
  );
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const setAll = (type: CodeType) => {
    setCodeTypes(Object.fromEntries(files.map(f => [f.file_id, type])));
  };

  const toggle = (fileId: string) => {
    setCodeTypes(prev => ({ ...prev, [fileId]: prev[fileId] === 'normal' ? 'flexible' : 'normal' }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setProgress(0);
    const overrides: Record<string, string> = {};
    let done = 0;
    for (const file of files) {
      try {
        const resp = await downloadCodeApi.generate(file.file_id, codeTypes[file.file_id]);
        const data = resp.data?.data;
        if (data?.download_filename) {
          overrides[file.file_id] = data.download_filename;
        }
      } catch (err: any) {
        // non-fatal: file will use original name
        console.warn('Code generation failed for', file.file_id, err.message);
      }
      done++;
      setProgress(Math.round((done / files.length) * 100));
    }
    setGenerating(false);
    onReady(overrides, folderMap);
  };

  const normalCount = Object.values(codeTypes).filter(t => t === 'normal').length;
  const flexCount = files.length - normalCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold">Download {files.length} Files</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Select campaign type for each file — codes will be embedded in filenames</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors" disabled={generating}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-muted-foreground">Mark all as:</span>
          <button
            onClick={() => setAll('normal')}
            disabled={generating}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Smartphone size={12} /> Normal
          </button>
          <button
            onClick={() => setAll('flexible')}
            disabled={generating}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Shuffle size={12} /> Flex
          </button>
          <span className="text-xs text-muted-foreground ml-auto">
            {normalCount} normal · {flexCount} flex
          </span>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-3 space-y-2">
          {files.map(file => (
            <div key={file.file_id} className="flex items-center justify-between gap-3 py-2 border-b border-border/50 last:border-0">
              <span className="text-sm truncate flex-1 text-foreground" title={file.original_filename}>
                {file.original_filename}
              </span>
              <button
                onClick={() => toggle(file.file_id)}
                disabled={generating}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                  codeTypes[file.file_id] === 'flexible'
                    ? 'border-purple-300 bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:border-purple-700 dark:text-purple-300'
                    : 'border-blue-300 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                }`}
              >
                {codeTypes[file.file_id] === 'flexible' ? <><Shuffle size={11} /> Flex</> : <><Smartphone size={11} /> Normal</>}
              </button>
            </div>
          ))}
        </div>

        {generating && (
          <div className="px-5 py-3 border-t border-border flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-muted rounded-full h-1.5">
                <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{progress}%</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Generating codes…</p>
          </div>
        )}

        {error && (
          <p className="px-5 py-2 text-xs text-red-600 dark:text-red-400 flex-shrink-0">{error}</p>
        )}

        <div className="px-5 py-4 border-t border-border flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 inline-flex items-center gap-2"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : null}
            {generating ? 'Generating…' : `Generate Codes & Download`}
          </button>
        </div>
      </div>
    </div>
  );
}
